/**
 * inboundAdapters — PURE mappers between `inbound_documents` and the FE shapes
 * the inbox needs (architecture-loop-a.md §5.10 / §10, WL-A6).
 *
 *  - `inboundToParsedZugferd(doc)`: maps an inbound document onto the
 *    `ReceivedInvoice` shape `ExpenseBookingModal` consumes, so "Als Ausgabe
 *    buchen" reuses the M4 flow 1:1 with `receiptStorageRef = originalPath`.
 *    Fail-closed: a missing field becomes `''` / `null` / `0` / `[]` — NEVER
 *    synthesized (§5.1a: "fehlende Felder → ''/null, NIE raten").
 *
 *  - `deriveInboundBadges(doc)`: maps a document onto the §10 badge set as
 *    i18n descriptors (key + params + tone), so the component renders the exact
 *    whitelist-approved wording via `t()` and tests assert on descriptors.
 *
 * Both are pure (no i/o, no React) — trivially unit-testable.
 *
 * @module inboundAdapters
 */

import type {
  ParsedZugferdData,
  ReceivedInvoice,
  ReceivedInvoiceTaxBreakdown,
} from '../types/receivedInvoiceTypes';
import type { InboundDocument, InboundTaxScheme } from '../types/inboundTypes';
import { resolveEffectiveTaxScheme } from './inboundTaxDecision';

// ---------------------------------------------------------------------------
// inboundToParsedZugferd
// ---------------------------------------------------------------------------

/** Finite number, else 0. */
function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** Non-empty string, else ''. */
function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Non-empty string, else null. */
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

/**
 * Builds a fail-closed `ReceivedInvoice` from an inbound document so the M4
 * `ExpenseBookingModal` can book it as an expense.
 *
 * `pdfStorageRef` is set to the WORM `originalPath`; the modal binds it to the
 * created expense as `receiptStorageRef` (`source:'zugferd'`). `documentTypeCode`
 * is deliberately `''` (NOT synthesized to '380') — the modal derives a standard
 * scheme from an empty code and the human confirms before booking.
 *
 * @param doc the inbound document.
 * @returns a `ReceivedInvoice` shaped for the expense-booking flow.
 */
export function inboundToParsedZugferd(doc: InboundDocument): ReceivedInvoice {
  const ex = doc.extracted;

  const taxBreakdown: ReceivedInvoiceTaxBreakdown[] = Array.isArray(
    ex?.taxBreakdown,
  )
    ? ex!.taxBreakdown.map((line) => ({
        taxRate: num(line?.taxRate),
        taxBasisAmount: num(line?.taxBasisAmount),
        taxAmount: num(line?.taxAmount),
        categoryCode: str(line?.categoryCode),
      }))
    : [];

  const iban = strOrNull(ex?.iban);

  const data: ParsedZugferdData = {
    documentNo: str(ex?.invoiceNumber),
    // Fail-closed: no BT-3 in the extracted subset → '' (modal → standard scheme).
    documentTypeCode: '',
    documentDate: strOrNull(ex?.documentDate),
    currency: str(ex?.currency),
    buyerReference: '',

    sellerName: str(ex?.sellerName),
    sellerStreet: '',
    sellerZip: '',
    sellerCity: '',
    sellerCountry: '',
    sellerEmail: '',
    sellerPhone: '',
    sellerTaxId: '',
    sellerVatId: str(ex?.sellerVatId),

    buyerName: str(ex?.buyerName),
    buyerStreet: '',
    buyerZip: '',
    buyerCity: '',
    buyerCountry: '',

    deliveryDate: strOrNull(ex?.leistungsdatum),

    grandTotal: num(ex?.grossTotal),
    dueAmount: 0,
    netTotal: num(ex?.netTotal),
    taxTotal: num(ex?.taxTotal),
    prepaidAmount: 0,

    dueDate: strOrNull(ex?.dueDate),
    paymentTerms: '',

    paymentMeans: iban ? [{ iban }] : [],

    taxBreakdown,
    lineItems: [],
  };

  const invoice: ReceivedInvoice = {
    id: doc.id,
    userId: doc.userId,
    uploadedAt:
      doc.receivedAt && typeof doc.receivedAt.toDate === 'function'
        ? doc.receivedAt.toDate().toISOString()
        : new Date().toISOString(),
    filename: str(doc.filename) || `${doc.id}`,
    data,
  };

  // Bind the WORM original as the expense receipt (M7b DATEV Belege-ZIP source).
  if (doc.originalPath) {
    invoice.pdfStorageRef = doc.originalPath;
  }

  return invoice;
}

// ---------------------------------------------------------------------------
// deriveInboundBadges
// ---------------------------------------------------------------------------

/**
 * Visual tone of a badge (drives colour, not text). `caution` (IDV-4) is a
 * softer signal than `warning` — "bitte prüfen" ambiguity (mixed/unknown tax
 * category) rather than a confirmed §13b/§1a self-assessment obligation.
 */
export type InboundBadgeTone =
  | 'success'
  | 'info'
  | 'warning'
  | 'neutral'
  | 'caution';

/**
 * A badge as an i18n descriptor. `i18nKey` is resolved against the
 * `posteingang` namespace; `params` feed interpolation (e.g. finding count).
 */
export interface InboundBadgeDescriptor {
  id: string;
  i18nKey: string;
  params?: Record<string, string | number>;
  tone: InboundBadgeTone;
}

/**
 * Derives the §10 badge set for a document.
 *
 * Exactly ONE primary classification badge (e-invoice-valid / business-rule /
 * format-error / sonstige / pending / other), followed by any applicable
 * secondary warnings (duplicate, deferred, sender-not-verified,
 * forwarding-damage). The senderAuth badge is 3-state: only shown when
 * `senderAuth` is present AND `senderAuthFailed` — an upload
 * (`senderAuth === null`) shows no auth badge.
 *
 * Not-yet-evaluated stubs (no `docClass`, or a non-terminal processing status:
 * `archived_pending_parse` / `deferred` / `failed_parse`) get a neutral
 * "wird ausgewertet" primary state INSTEAD of the `other` fallback — a
 * pending invoice must not be mislabelled as a non-invoice file (§5.2, e-rechnung
 * Go-Live finding B). The `businessRuleError` badge carries NO finding count:
 * the findings are not surfaced anywhere in v1 (`reportPath == null`), so the
 * badge must not promise a detail the user cannot see (e-rechnung finding C).
 *
 * @param doc the inbound document.
 * @returns ordered badge descriptors (primary first).
 */
export function deriveInboundBadges(
  doc: InboundDocument,
): InboundBadgeDescriptor[] {
  const badges: InboundBadgeDescriptor[] = [];
  const status = doc.validation?.status;

  // A stub that `processInboundDocument` has not classified yet: no docClass, or
  // a processing status that is not a terminal, evaluated outcome.
  const notYetEvaluated =
    !doc.docClass ||
    doc.processingStatus === 'archived_pending_parse' ||
    doc.processingStatus === 'deferred' ||
    doc.processingStatus === 'failed_parse';

  // ── Primary classification badge ─────────────────────────────────────────
  if (doc.docClass === 'e_invoice' && status === 'valid') {
    badges.push({
      id: 'eInvoiceValid',
      i18nKey: 'badge.eInvoiceValid',
      tone: 'success',
    });
  } else if (doc.docClass === 'e_invoice' && status === 'business_rule_error') {
    // No count: findings are not rendered in v1 (reportPath == null) — do not
    // promise a detail the user cannot open (e-rechnung finding C).
    badges.push({
      id: 'businessRuleError',
      i18nKey: 'badge.businessRuleError',
      tone: 'warning',
    });
  } else if (status === 'format_error') {
    badges.push({
      id: 'formatError',
      i18nKey: 'badge.formatError',
      tone: 'warning',
    });
  } else if (doc.docClass === 'sonstige_rechnung') {
    badges.push({
      id: 'sonstigeRechnung',
      i18nKey: 'badge.sonstigeRechnung',
      tone: 'neutral',
    });
  } else if (notYetEvaluated) {
    // Still being processed — neutral, no classification/compliance claim.
    badges.push({ id: 'pending', i18nKey: 'badge.pending', tone: 'neutral' });
  } else {
    // docClass 'other' (zip/docx/…) — neutral, no compliance claim.
    badges.push({ id: 'other', i18nKey: 'badge.other', tone: 'neutral' });
  }

  // ── Tax-scheme badge (IDV-4, architecture-inbound-datev.md §5.7) ─────────
  // Additive, derived from the server-persisted `doc.taxScheme` — independent
  // of the primary e-invoice/format classification above. Skipped while the
  // document is still `notYetEvaluated` (pending/deferred/failed_parse): the
  // tax scheme has not been derived yet, so flagging it would be premature —
  // same reasoning as the primary "wird ausgewertet" gate above. A
  // missing/legacy `taxScheme` on an already-evaluated document is treated the
  // same as `'unknown'` (architecture §4.2: "FE defaultet null → Steuerkategorie
  // unbestimmt") — fail-closed, never silently neutral.
  if (!notYetEvaluated) {
    // Manuelle Kontierung (architecture-eingang-steuerkontierung.md §6.5): das
    // effektive Scheme kommt aus `resolveEffectiveTaxScheme` (Auto-Wert ODER eine
    // aktive, nicht-stale manuelle Entscheidung). Ohne Entscheidung ist es
    // byte-identisch zu `doc.taxScheme ?? 'unknown'` (Bestand bleibt grün).
    const effective = resolveEffectiveTaxScheme(doc);
    const taxScheme: InboundTaxScheme = effective.scheme;
    const isManual = effective.source === 'manual';
    if (taxScheme === 'reverse_charge' || taxScheme === 'reverse_charge_mixed') {
      badges.push({
        id: 'reverseCharge13b',
        i18nKey: 'badge.reverseCharge13b',
        // i18next `context` selects `badge.reverseCharge13b_mixed` when set,
        // falling back to `badge.reverseCharge13b` otherwise — keeps ONE
        // i18nKey while letting the mixed case add "+ regelbesteuerte
        // Positionen" (§5.1 Regel 4 / modules-inbound-datev.md IDV-4). The §13b
        // liability hint deliberately STAYS even for a manually set scheme (J-2).
        params:
          taxScheme === 'reverse_charge_mixed' ? { context: 'mixed' } : undefined,
        tone: 'warning',
      });
    } else if (taxScheme === 'intra_community') {
      badges.push({
        id: 'intraCommunity',
        i18nKey: 'badge.intraCommunity',
        tone: 'warning',
      });
    } else if (taxScheme === 'mixed' && !isManual) {
      // „Bitte prüfen"-Ambiguität — entfällt, sobald die Nutzer:in aktiv
      // kontiert hat (§6.5: taxSchemeUnknown/mixedCategories entfallen bei
      // aktiver Entscheidung; das „Manuell kontiert"-Badge tritt an ihre Stelle).
      badges.push({
        id: 'mixedCategories',
        i18nKey: 'badge.mixedCategories',
        tone: 'caution',
      });
    } else if (taxScheme === 'unknown') {
      // reviewer 🟡-6: NOT neutral/green — a §13b document with a failed
      // categoryCode extraction must stay visibly flagged. Only the auto path
      // reaches 'unknown' (a manual decision never resolves to 'unknown').
      badges.push({
        id: 'taxSchemeUnknown',
        i18nKey: 'badge.taxSchemeUnknown',
        tone: 'caution',
      });
    }
    // 'standard' / 'tax_exempt' / 'zero_rated' / 'export' / 'out_of_scope' →
    // neutral, no extra badge (§5.7: "neutral (kein Extra-Badge)").

    // „Manuell kontiert"-Marker (§6.5) — additiv bei aktiver, nicht-staler
    // Entscheidung, unabhängig vom Effektiv-Scheme (tone `info`, keine
    // Compliance-Zusage — nur „vom Nutzer selbst festgelegt").
    if (isManual) {
      badges.push({
        id: 'taxSchemeManual',
        i18nKey: 'badge.taxSchemeManual',
        tone: 'info',
      });
    }
  }

  // ── Secondary badges ─────────────────────────────────────────────────────
  if (typeof doc.duplicateOfId === 'string' && doc.duplicateOfId.length > 0) {
    badges.push({
      id: 'duplicate',
      i18nKey: 'badge.duplicate',
      params: { n: doc.duplicateOfId },
      tone: 'info',
    });
  }
  if (doc.processingStatus === 'deferred') {
    badges.push({ id: 'deferred', i18nKey: 'badge.deferred', tone: 'info' });
  }
  // 3-state: only email docs carry senderAuth; upload/claim → null → no badge.
  if (doc.senderAuth && doc.senderAuth.senderAuthFailed) {
    badges.push({
      id: 'senderAuthFailed',
      i18nKey: 'badge.senderAuthFailed',
      tone: 'warning',
    });
  }
  if (doc.integrity?.forwardingDamageSuspected) {
    badges.push({
      id: 'forwardingDamage',
      i18nKey: 'badge.forwardingDamage',
      tone: 'warning',
    });
  }

  return badges;
}
